import { Platform, View } from 'react-native';
import { Text } from 'react-native-paper';
import { Colors } from '../../themes/colors';
import { TypeIcon } from '../crop-circle/Components/Type';
import { tokens } from '../crop-circle/tokens';
import { themedStyleSheet } from '../../themes/themedStyleSheet';

export interface IntegrationReadingRowProps {
  /** Resolved routine display name (e.g. "Odometer"). */
  routineName: string;
  /** New counter value. */
  count?: number;
  /** Previous counter value, when known — drives the "old → new" transition. */
  oldValue?: number;
  /** Unit of measure (e.g. "mi", "hrs"). */
  uom?: string;
  /** Pre-formatted relative/short time string. */
  when: string;
  testID?: string;
}

const formatValue = (value: number, uom?: string): string =>
  uom ? `${value} ${uom}` : `${value}`;

/**
 * A lightweight, NON-navigating activity row for an integration-sourced counter
 * reading on the asset detail surfaces (Activity fold + full asset activity
 * list). Rendered read-time from an existing `assetroutine` doc — never a
 * separately written record — so there is no Issue to open and the row is not
 * pressable.
 *
 * Visually it tracks the `IssueCardCompact` idiom (routines-blue type icon,
 * emphasised title, secondary timestamp on the right, secondary detail line)
 * so it reads as a peer of the issue rows it sits beside.
 *
 * The value line shows the transition `old → new` when `oldValue` is present
 * (which it always is for a changed reading), or just the new value otherwise.
 */
export const IntegrationReadingRow = ({
  routineName,
  count,
  oldValue,
  uom,
  when,
  testID,
}: IntegrationReadingRowProps) => {
  const hasCount =
    count !== undefined && count !== null && !Number.isNaN(count);
  const hasOld =
    oldValue !== undefined && oldValue !== null && !Number.isNaN(oldValue);

  const valueText = hasCount
    ? hasOld
      ? `${formatValue(oldValue as number, uom)} → ${formatValue(
          count as number,
          uom
        )}`
      : formatValue(count as number, uom)
    : '';

  return (
    <View style={styles.card} testID={testID}>
      <View style={styles.topRow}>
        <View style={styles.titleRow}>
          <TypeIcon variant="routines" size={16} />
          <Text style={styles.title} numberOfLines={1}>
            {routineName}
          </Text>
        </View>
        <Text style={styles.when} numberOfLines={1}>
          {when}
        </Text>
      </View>
      {!!valueText && (
        <Text style={styles.value} numberOfLines={1}>
          {valueText}
        </Text>
      )}
    </View>
  );
};

IntegrationReadingRow.displayName = 'IntegrationReadingRow';

const styles = themedStyleSheet((role) => ({
  card: {
    width: '100%',
    alignSelf: 'stretch',
    paddingVertical: tokens.spacing.md,
    paddingHorizontal: tokens.spacing.lg,
    backgroundColor: role.surfaceHover,
    marginBottom: tokens.spacing.xs,
    gap: tokens.spacing.xs,
    ...Platform.select({
      android: { elevation: 0 },
      default: {},
    }),
    shadowColor: Colors.black,
    shadowOpacity: 0.02,
    shadowRadius: 4,
    shadowOffset: { width: 0, height: 1 },
  },
  topRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 10,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flex: 1,
    minWidth: 0,
  },
  title: {
    flexShrink: 1,
    minWidth: 0,
    ...tokens.typography.label.largeEmphasised,
    color: role.textStrong,
  },
  when: {
    ...tokens.typography.label.medium,
    color: role.textSecondary,
    marginLeft: 'auto',
  },
  value: {
    ...tokens.typography.label.large,
    color: role.textSecondary,
  },
}));
